import { NoteEditorSourceBarChip } from "@/components/note/editor/source-bar/note-editor-source-bar-chip";
import { INoteObservable } from "@/store/note/types";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useRef } from "react";
import { generateRecentDateString } from "@/domains/date/date";
import {
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextOverflow,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSFadeHorizontalScroll,
} from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";

interface NoteEditorBottomBarProps {
  note: INoteObservable;
  tooltipLabel: string;
  containerWidth?: number;
}

export const NoteEditorBottomBar = observer<NoteEditorBottomBarProps>(
  ({ note, tooltipLabel, containerWidth }) => {
    const sources = note.sourceList?.allSupportedSources ?? [];

    const playingSourceIdRef = useRef<string | null>(null);

    const collapsedChips =
      typeof containerWidth === "number" && containerWidth <= MDSPanelWidths.narrow;

    const hasSuggestion = !!note?.suggestionData;

    return (
      <Container $hasSuggestion={hasSuggestion}>
        <ScrollableArea>
          <MDSFadeHorizontalScroll>
            <InnerContainer>
              {note.isShared && (
                <MDSIcon kind={MDSIconKind.Shared} size={10} color={MDSIconColor.Secondary} />
              )}
              {!note.isShared && (
                <MDSIcon kind={MDSIconKind.Lock} size={10} color={MDSIconColor.Secondary} />
              )}
              <MDSTooltip
                config={{
                  label: tooltipLabel,
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSText
                  variant={MDSTextVariant.TextXs}
                  numberOfLines={1}
                  overflow={MDSTextOverflow.Ellipsis}
                >
                  {note.isShared && "Shared"}
                  {!note.isShared && "Private"}
                  {" • "}
                  Edited{" "}
                  {generateRecentDateString(note.modifiedAt, {
                    relativeDateShouldBeLowercase: true,
                  })}{" "}
                  by {note.authors?.[note.authors.length - 1]?.profileDisplayName}
                </MDSText>
              </MDSTooltip>
              <Spacer />
              {sources.length > 0 && (
                <MDSIcon kind={MDSIconKind.Paperclip} size={12} color={MDSIconColor.Secondary} />
              )}
              {sources.map(source => (
                <NoteEditorSourceBarChip
                  isCollapsed={collapsedChips}
                  key={source.id}
                  source={source}
                  note={note}
                  playingSourceIdRef={playingSourceIdRef}
                />
              ))}
            </InnerContainer>
          </MDSFadeHorizontalScroll>
        </ScrollableArea>
      </Container>
    );
  }
);
NoteEditorBottomBar.displayName = "NoteEditorBottomBar";

const Container = styled.div<{ $hasSuggestion: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: ${({ theme }) =>
    `${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["7"]}`};
  background-color: ${({ theme, $hasSuggestion }) =>
    $hasSuggestion ? theme.colors.background.diff : theme.colors.surface.pane};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-top: 1px solid ${({ theme }) => theme.colors.surface.chrome};
`;

const ScrollableArea = styled.div`
  flex-grow: 1;
  /* Hide the native scrollbar, MDSFadeHorizontalScroll provides its own */
  overflow: hidden;
`;

const Spacer = styled.div`
  flex: 1;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  min-width: 100%;
`;
